import { useState, useEffect } from 'react';
import {
  Box, Chip, Tooltip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  List, ListItemButton, Typography, CircularProgress, Alert, InputAdornment,
} from '@mui/material';
import {
  Link as LinkIcon, LinkOff as UnlinkIcon, Sync as SyncIcon,
  MoreVert as MoreIcon, Search as SearchIcon, AccountTree as CostCenterIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

/**
 * Vínculo cliente ↔ centro de costo de Talana (PLAN_INTEGRACION_TALANA §4.3).
 * En Talana el cliente ES un centro de costo (codigo = RUT auxiliar). El
 * backend vincula o crea el nodo en segundo plano al crear el cliente; aquí
 * se muestra el estado (externalRefs.talana) y se puede reintentar, vincular
 * a mano a un nodo del catálogo sincronizado, o desvincular.
 */
const ErpClientTalanaLink = ({ client, onChanged }) => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert } = useStore.getState();
  const headers = { headers: { Authorization: `Bearer ${authToken}` } };

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [working, setWorking] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [search, setSearch] = useState('');

  const ref = client?.externalRefs?.talana || null;

  useEffect(() => {
    if (!pickerOpen) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        setCatalogLoading(true);
        const response = await api.get('/erp/talana/catalogs/costCenters', {
          params: { organizationId: organization, active: 'all' },
          ...headers,
        });
        if (!cancelled && response.data.response?.status_response === 'OK') {
          setCatalog(response.data.response.data || []);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) showAlert(t('erp.clients.talana.catalogError'), 'error');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerOpen]);

  const run = async (fn, successKey) => {
    try {
      setWorking(true);
      const response = await fn();
      const body = response.data.response;
      if (body?.status_response === 'OK') {
        showAlert(t(successKey), 'success');
      } else {
        showAlert(body?.data?.lastError?.message || t('erp.clients.talana.error'), 'error');
      }
      onChanged?.();
    } catch (err) {
      console.error(err);
      const code = err.response?.status;
      const message = err.response?.data?.response?.message || err.response?.data?.errorMessage;
      if (code === 503) showAlert(t('erp.talana.notConfigured'), 'warning');
      else showAlert(message || t('erp.clients.talana.error'), 'error');
      onChanged?.();
    } finally {
      setWorking(false);
      setMenuAnchor(null);
    }
  };

  const handleSync = () =>
    run(() => api.post(`/erp/clients/${client.uid}/talana/sync`, { organizationId: organization }, headers), 'erp.clients.talana.synced');

  const handleUnlink = () =>
    run(() => api.delete(`/erp/clients/${client.uid}/talana/link`, { params: { organizationId: organization }, ...headers }), 'erp.clients.talana.unlinked');

  const handlePick = (item) => {
    setPickerOpen(false);
    run(() => api.post(`/erp/clients/${client.uid}/talana/link`, { organizationId: organization, talanaId: item.talanaId }, headers), 'erp.clients.talana.linked');
  };

  const chip = () => {
    if (working) return <Chip size="small" icon={<CircularProgress size={12} color="inherit" />} label={t('erp.clients.talana.working')} />;
    if (!ref) return <Chip size="small" variant="outlined" label={t('erp.clients.talana.none')} />;
    if (ref.status === 'linked' || ref.status === 'created') {
      return (
        <Tooltip title={`${t('erp.clients.talana.costCenter')}: ${ref.name || ''} · ${t('erp.talana.detail.table.code')}: ${ref.code || '—'} · ID ${ref.id}${ref.lastError?.code === 'PATCH_UNSUPPORTED' ? ` · ${t('erp.clients.talana.patchUnsupported')}` : ''}`}>
          <Chip size="small" color={ref.lastError ? 'warning' : 'success'} variant="outlined" icon={<CostCenterIcon />}
            label={`Talana · ${ref.code || ref.name || ref.id}`} />
        </Tooltip>
      );
    }
    if (ref.status === 'failed') {
      return (
        <Tooltip title={ref.lastError?.message || ''}>
          <Chip size="small" color="error" variant="outlined" label={t('erp.clients.talana.failed')} />
        </Tooltip>
      );
    }
    return <Chip size="small" color="default" variant="outlined" label={t('erp.clients.talana.pending')} />;
  };

  const needle = search.trim().toLowerCase();
  const filtered = (needle
    ? catalog.filter((i) => String(i.name || '').toLowerCase().includes(needle) || String(i.code || '').toLowerCase().includes(needle) || String(i.talanaId).includes(needle))
    : catalog
  ).slice(0, 50);

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {chip()}
      <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} disabled={working}>
        <MoreIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={handleSync}>
          <ListItemIcon><SyncIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('erp.clients.talana.sync')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); setSearch(''); setPickerOpen(true); }}>
          <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('erp.clients.talana.pick')}</ListItemText>
        </MenuItem>
        {ref && (
          <MenuItem onClick={handleUnlink}>
            <ListItemIcon><UnlinkIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t('erp.clients.talana.unlink')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('erp.clients.talana.pickTitle')}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('erp.clients.talana.pickHint', { code: client.auxiliaryRut || '—' })}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t('erp.talana.detail.searchPlaceholder')} sx={{ mb: 1 }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }} />
          {catalogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : catalog.length === 0 ? (
            <Alert severity="info">{t('erp.clients.talana.catalogEmpty')}</Alert>
          ) : (
            <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
              {filtered.map((item) => (
                <ListItemButton key={item.uid} onClick={() => handlePick(item)} selected={ref?.id === item.talanaId}>
                  <ListItemText
                    primary={item.name}
                    secondary={`${t('erp.talana.detail.table.code')}: ${item.code || '—'} · ID ${item.talanaId}${item.parent ? ` · ${t('erp.talana.detail.table.parent')} ${item.parent}` : ''}${item.active === false ? ` · ${t('erp.common.inactive')}` : ''}`}
                  />
                </ListItemButton>
              ))}
              {filtered.length === 50 && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>{t('erp.clients.talana.moreResults')}</Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)} sx={{ textTransform: 'none' }}>{t('erp.common.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErpClientTalanaLink;
